import { TextField } from '@mui/material'
import React from 'react'
import { useForm, Controller, FormProvider } from 'react-hook-form'

import { LoadingButton } from 'components/common/LoadingButton'
import {
  getGcpAuthenticationFormInitializersNew,
  type GcpAuthenticationFormValuesNew
} from 'components/common/mapped-entities/gcp/authentication/gcp-auth-form-utils'
import { getGcpAuthenticationPayload } from 'components/common/mapped-entities/gcp/authentication/gcp-integration-utils'
import { GcpAuthenticationSection } from 'components/common/mapped-entities/gcp/authentication/GcpAuthenticationSection'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import { errorToast } from 'lib/toast'
import { useCreateAuthentication } from 'queries/external-integrations/use-create-authentication'
import { type TeamExternalIntegrationGcp } from 'types/api/status-checks/status-check'
import { handleDataFetchOnChange } from 'utils/handle-data-fetch-on-change'
import { messageFromResponseError } from 'utils/strings'
import { type OnSuccessExternalIntegration } from 'utils/team-external-integrations/team-external-integrations-util'

const FORM_FIELD_MAX_WIDTH = 500

export function GcpNewAuthenticationForm({
  gcpExternalIntegrations,
  onSuccess,
  templateUrl
}: {
  gcpExternalIntegrations: TeamExternalIntegrationGcp[]
  onSuccess?: OnSuccessExternalIntegration
  templateUrl: string
}) {
  const { reFetchExternalIntegrations } =
    useStatusCheckExternalIntegrationsContext()

  const {
    isLoading: isLoadingCreateAuthentication,
    mutateAsync: createAuthentication
  } = useCreateAuthentication()

  const authForm = useForm<GcpAuthenticationFormValuesNew>({
    ...getGcpAuthenticationFormInitializersNew(gcpExternalIntegrations),
    mode: 'all'
  })

  const { control, formState, getValues } = authForm
  const { isSubmitting } = formState

  const handleSubmitAuthentication = async () => {
    try {
      const { projectId, serviceAccountId } = getValues()
      const body = getGcpAuthenticationPayload(projectId, serviceAccountId)
      const response = await createAuthentication(body)
      onSuccess?.(response)
      await handleDataFetchOnChange(reFetchExternalIntegrations)
    } catch (error) {
      errorToast(messageFromResponseError(error))
    }
  }

  /* TODO: replace when ready
  const isSubmitDisabled = !isValid
  */
  const isSubmitDisabled = true

  const isSubmitLoading = isLoadingCreateAuthentication || isSubmitting

  return (
    <FormProvider {...authForm}>
      <GcpAuthenticationSection templateUrl={templateUrl}>
        <Controller
          control={control}
          name="projectId"
          render={({ field, fieldState: { error } }) => (
            <TextField
              error={Boolean(error?.message)}
              helperText={error?.message}
              label="Project ID"
              sx={{
                width: '100%',
                maxWidth: FORM_FIELD_MAX_WIDTH,
                marginTop: 1
              }}
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name="serviceAccountId"
          render={({ field, fieldState: { error } }) => (
            <TextField
              error={Boolean(error?.message)}
              helperText={error?.message}
              label="Service Account ID"
              sx={{
                width: '100%',
                maxWidth: FORM_FIELD_MAX_WIDTH,
                marginTop: 1
              }}
              {...field}
            />
          )}
        />
      </GcpAuthenticationSection>
      <LoadingButton
        isDisabled={isSubmitDisabled}
        isLoading={isSubmitLoading}
        sx={{ marginTop: 1 }}
        variant="contained"
        onClick={handleSubmitAuthentication}
      >
        {isSubmitLoading ? 'Saving' : 'Save'}
      </LoadingButton>
    </FormProvider>
  )
}
